import { atom } from 'ximple/atoms';
import { NativeCalculateResult, NativeMemberCardSelection } from '@repo/types';
import {
    loadNativeCoverageSnapshot,
    storeNativeCoverageSnapshot,
} from './nativeCoverage.localstorage';

// Shared, non-persisted source of truth for native member-card coverage at checkout.
// Written by useNativeMemberCoverageEffects (the app-level calculate poller); read by the selection
// panel, the summary, the pay button, and usePayment's reserve threading.
export type NativeMemberCoverageState = NativeMemberCardSelection & {
    result: NativeCalculateResult | null;
    // Frozen snapshot of `result` taken at reserve, so the confirmation/receipt keeps showing the
    // member-card coverage + actual total after the cart clears (which nulls the live result).
    committedResult: NativeCalculateResult | null;
    isCalculating: boolean;
    error: boolean;
    retryNonce: number;
};

export const nativeMemberCoverageInitialState: NativeMemberCoverageState = {
    lineOverrides: {},
    excludedLineKeys: [],
    lineGroups: {},
    result: null,
    committedResult: null,
    isCalculating: false,
    error: false,
    retryNonce: 0,
};

export const nativeMemberCoverageAtom = atom<NativeMemberCoverageState>({
    initialValue: nativeMemberCoverageInitialState,
});

// Drop per-sub-group override/exclude keys (`${lineKey}:gN`) whose index is >= keepCount — the
// sub-groups a new split or merge dropped (merge passes keepCount 0 → clears them all).
function pruneSubGroups(
    cur: NativeMemberCardSelection,
    lineKey: string,
    keepCount: number,
): Pick<NativeMemberCardSelection, 'lineOverrides' | 'excludedLineKeys'> {
    const prefix = `${lineKey}:g`;
    const isDropped = (k: string) => {
        if (!k.startsWith(prefix)) return false;
        const rest = k.slice(prefix.length);
        return /^\d+$/.test(rest) && Number(rest) >= keepCount;
    };
    return {
        lineOverrides: Object.fromEntries(
            Object.entries(cur.lineOverrides).filter(([k]) => !isDropped(k)),
        ),
        excludedLineKeys: cur.excludedLineKeys.filter((k) => !isDropped(k)),
    };
}

// Per-line selection setters, called from the selection panel. A null cardId clears the override.
// Override and exclusion are mutually exclusive: setting one clears the other for that line.
export function setMemberCardLineOverride(lineKey: string, cardId: string | null): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    const lineOverrides = { ...cur.lineOverrides };
    if (cardId) lineOverrides[lineKey] = cardId;
    else delete lineOverrides[lineKey];
    nativeMemberCoverageAtom.update({
        ...cur,
        lineOverrides,
        excludedLineKeys: cardId
            ? cur.excludedLineKeys.filter((k) => k !== lineKey)
            : cur.excludedLineKeys,
    });
}

export function setMemberCardLineExcluded(lineKey: string, excluded: boolean): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    const excludedLineKeys = excluded
        ? Array.from(new Set([...cur.excludedLineKeys, lineKey]))
        : cur.excludedLineKeys.filter((k) => k !== lineKey);
    const lineOverrides = { ...cur.lineOverrides };
    if (excluded) delete lineOverrides[lineKey]; // mutual exclusion
    nativeMemberCoverageAtom.update({ ...cur, excludedLineKeys, lineOverrides });
}

// Split a ticket category into ticket sub-groups (sizes). Each sub-group covers independently under
// a `${lineKey}:gN` key, reusing the override/exclude setters. Drops sub-group keys now out of range.
export function splitMemberCardLine(lineKey: string, sizes: number[]): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    nativeMemberCoverageAtom.update({
        ...cur,
        ...pruneSubGroups(cur, lineKey, sizes.length),
        lineGroups: { ...cur.lineGroups, [lineKey]: sizes },
    });
}

// Collapse a split back to a single auto line, clearing all of its sub-group overrides/excludes.
export function mergeMemberCardLine(lineKey: string): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    const { [lineKey]: _removed, ...lineGroups } = cur.lineGroups;
    nativeMemberCoverageAtom.update({ ...cur, ...pruneSubGroups(cur, lineKey, 0), lineGroups });
}

// Remove one sub-group, re-keying the higher sub-groups' overrides/excludes down one so each card
// follows its group; collapses to a single auto line if fewer than two groups remain.
export function removeMemberCardSubGroup(lineKey: string, index: number): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    const current = cur.lineGroups[lineKey];
    if (!current) return;
    const nextSizes = current.filter((_size, i) => i !== index);

    const prefix = `${lineKey}:g`;
    const remap = (k: string): string | null => {
        if (!k.startsWith(prefix)) return k;
        const rest = k.slice(prefix.length);
        if (!/^\d+$/.test(rest)) return k;
        const n = Number(rest);
        if (n === index) return null;
        return n > index ? `${prefix}${n - 1}` : k;
    };
    const lineOverrides: { [k: string]: string } = {};
    for (const [k, v] of Object.entries(cur.lineOverrides)) {
        const nk = remap(k);
        if (nk !== null) lineOverrides[nk] = v;
    }
    const excludedLineKeys: string[] = [];
    for (const k of cur.excludedLineKeys) {
        const nk = remap(k);
        if (nk !== null) excludedLineKeys.push(nk);
    }

    // Fewer than two groups left → collapse to a single auto line, clearing leftover sub-keys.
    if (nextSizes.length < 2) {
        const { [lineKey]: _group, ...lineGroups } = cur.lineGroups;
        nativeMemberCoverageAtom.update({
            ...cur,
            ...pruneSubGroups({ ...cur, lineOverrides, excludedLineKeys }, lineKey, 0),
            lineGroups,
        });
        return;
    }
    nativeMemberCoverageAtom.update({
        ...cur,
        lineOverrides,
        excludedLineKeys,
        lineGroups: { ...cur.lineGroups, [lineKey]: nextSizes },
    });
}

export function resetMemberCardCoverage(): void {
    nativeMemberCoverageAtom.update(nativeMemberCoverageInitialState);
}

// Freeze the current coverage as the committed snapshot at reserve time, so the confirmation/receipt
// reflects how the member actually paid even after the cart (and the live result) are cleared.
export function commitMemberCardCoverage(): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    nativeMemberCoverageAtom.update({ ...cur, committedResult: cur.result });
    // Persist the snapshot so the confirmation/receipt survives a payment-gateway redirect/reload
    // (the in-memory committedResult is lost on reload). Dropped again when a fresh checkout opens.
    storeNativeCoverageSnapshot(cur.result);
}

// Clear the live preview (result/loading/error) but KEEP the committed snapshot — used when the cart
// empties (e.g. a completed booking clears it) so the confirmation/receipt still shows how the member
// paid. No-op guard keeps guest/legacy byte-identical.
export function clearLiveCoverage(): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    if (cur.result === null && !cur.isCalculating && !cur.error) return;
    nativeMemberCoverageAtom.update({ ...cur, result: null, isCalculating: false, error: false });
}

// Drop the committed snapshot (in-memory + localStorage). Called when a fresh shopping session starts
// (cart goes empty → non-empty) so a previous booking's coverage can't leak into the new cart.
export function clearCommittedCoverage(): void {
    storeNativeCoverageSnapshot(null);
    const cur = nativeMemberCoverageAtom.subject.value;
    if (cur.committedResult === null) return;
    nativeMemberCoverageAtom.update({ ...cur, committedResult: null });
}

// Restore the committed snapshot from localStorage on a payment-gateway redirect-return confirmation,
// where the in-memory committedResult was lost on reload.
export function restoreCommittedCoverageFromSnapshot(): void {
    const snapshot = loadNativeCoverageSnapshot();
    if (!snapshot) return;
    const cur = nativeMemberCoverageAtom.subject.value;
    if (cur.committedResult === null) {
        nativeMemberCoverageAtom.update({ ...cur, committedResult: snapshot });
    }
}

// Re-trigger the coverage preview after a failed calculate. Bumps a nonce the poller's request
// memo depends on, so the effect re-runs even though the cart and selection are unchanged.
export function retryMemberCardCoverage(): void {
    const cur = nativeMemberCoverageAtom.subject.value;
    nativeMemberCoverageAtom.update({ ...cur, error: false, retryNonce: cur.retryNonce + 1 });
}
